/**
 * Protected route component - requires authentication and optional permissions
 */

import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermissions?: string[];
}

export default function ProtectedRoute({
  children,
  requiredPermissions = [],
}: ProtectedRouteProps): JSX.Element {
  const { currentUser, userData, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-800 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    const redirectPath = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(redirectPath)}`} replace />;
  }

  // Check permissions if required
  if (requiredPermissions.length > 0 && userData) {
    const hasPermission = requiredPermissions.some((required) => {
      // Check for exact permission or wildcard
      const [resource] = required.split(':');
      return (
        userData.permissions.includes(required) ||
        userData.permissions.includes(`${resource}:*`)
      );
    });

    if (!hasPermission) {
      return (
        <div className="container-custom py-12">
          <div className="max-w-md mx-auto text-center">
            <h1 className="text-4xl font-bold text-red-600 mb-4">Access Denied</h1>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              You don't have permission to access this page.
            </p>
            <a href="/" className="btn btn-primary">
              Go Home
            </a>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
}
